import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import axios from 'axios';

// Backend URL
const BACKEND_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';

// Token storage key
const TOKEN_KEY = 'admin_token';
const TOKEN_EXPIRY_KEY = 'admin_token_expiry';

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (pin: string) => Promise<{ success: boolean; error?: string }>;
    logout: () => void;
    getToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Configure axios interceptor to add auth header.
// Respeta un Authorization explícito: las consultas del jugador (Mi Perfil, encuesta,
// mercadito) mandan SU token; si se pisaba con el admin_token daban 401 y el bloque de
// abajo borraba la sesión de admin (el admin que también entró con su PIN de jugador
// terminaba con "Cuentas jugadores" pidiéndole el PIN de admin).
axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Handle 401 responses: solo se borra el admin_token si ESA consulta iba con él
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const token = localStorage.getItem(TOKEN_KEY);
        const enviado = error.config?.headers?.Authorization;
        if (error.response?.status === 401 && token && enviado === `Bearer ${token}`) {
            // Clear invalid token
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(TOKEN_EXPIRY_KEY);
        }
        return Promise.reject(error);
    }
);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Check for existing valid token on mount
    useEffect(() => {
        const checkAuth = () => {
            const token = localStorage.getItem(TOKEN_KEY);
            const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

            if (token && expiry) {
                const expiryTime = parseInt(expiry, 10);
                if (Date.now() < expiryTime) {
                    setIsAuthenticated(true);
                } else {
                    // Token expired, clean up
                    localStorage.removeItem(TOKEN_KEY);
                    localStorage.removeItem(TOKEN_EXPIRY_KEY);
                }
            }
            setIsLoading(false);
        };

        checkAuth();
    }, []);

    const login = async (pin: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await axios.post(`${BACKEND_URL}/api/auth/login`, { pin });

            if (response.data.access_token) {
                const { access_token, expires_in } = response.data;

                // Store token and expiry
                localStorage.setItem(TOKEN_KEY, access_token);
                localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + expires_in * 1000));

                setIsAuthenticated(true);
                return { success: true };
            }

            return { success: false, error: 'Respuesta inválida del servidor' };
        } catch (error: any) {
            const message = error.response?.data?.detail || 'Error de conexión';
            return { success: false, error: message };
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        setIsAuthenticated(false);
    };

    const getToken = (): string | null => {
        return localStorage.getItem(TOKEN_KEY);
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout, getToken }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default useAuth;
